/**
 * שדה הטלפון — TASKS 4.4 · PRD 7.2, "שלוש שכבות".
 *
 * שכבה 1 (כאן): `type="tel"` · `inputmode="tel"` · `dir="ltr"` · מיסוך חי ·
 *   ניקוי בהדבקה · ולידציה מיידית עם הודעה בעברית ודוגמה.
 * שכבה 2 (שרת): `normalizePhone` רץ שוב ב-`createQuote` — ⛔ אין תלות בזה.
 * שכבה 3: `RequestPage` מציג את אישור המספר החוזר לפני שליחה סופית.
 *
 * ⭐ **הוולידציה עצמה — `normalizePhone` מ-`@noam/shared`, אותה פונקציה
 *    בדיוק שרצה בשרת.** המיסוך החי כאן הוא תצוגה בלבד; הוא ⛔ אינו קובע מה
 *    תקין — `normalizePhone` קובע.
 */
import { useId, useState } from 'react';
// ⚠️ תת-נתיב, ⛔ לא הבארל `@noam/shared` — ראו ההערה ב-CartPage.tsx
import { normalizePhone, type NormalizePhoneResult } from '@noam/shared/phone';
import { FieldError } from '../components/FieldError.js';

/**
 * מיסוך חי לתצוגה בלבד — קיבוץ 3-3-4 בסגנון סלולרי (הדוגמה ב-TASKS 4.4:
 * `050-123-4567`). ⛔ ⛔ **אינו** קובע תקינות — ראו `normalizePhone` למעלה.
 */
function liveMask(raw: string): string {
  const cleaned = raw.replace(/[^\d+]/g, '');
  let national = cleaned;
  if (cleaned.startsWith('+972')) national = `0${cleaned.slice(4)}`;
  else if (cleaned.startsWith('00972')) national = `0${cleaned.slice(5)}`;
  else if (cleaned.startsWith('972') && !cleaned.startsWith('0')) national = `0${cleaned.slice(3)}`;

  const digits = national.replace(/\D/g, '').slice(0, 10);
  const p1 = digits.slice(0, 3);
  const p2 = digits.slice(3, 6);
  const p3 = digits.slice(6, 10);
  return [p1, p2, p3].filter(Boolean).join('-');
}

export interface PhoneFieldProps {
  label: string;
  onResult: (result: NormalizePhoneResult, displayValue: string) => void;
}

export function PhoneField({ label, onResult }: PhoneFieldProps) {
  const fieldId = useId();
  const errorId = useId();

  const [display, setDisplay] = useState('');
  const [touched, setTouched] = useState(false);

  const result = normalizePhone(display);
  const showError = touched && display.trim() !== '' && !result.ok;

  function handleChange(rawValue: string) {
    const masked = liveMask(rawValue);
    setDisplay(masked);
    onResult(normalizePhone(masked), masked);
  }

  return (
    <div>
      <label htmlFor={fieldId} className="block text-sm font-semibold">
        {label}*
      </label>
      <input
        id={fieldId}
        name="phone"
        type="tel"
        inputMode="tel"
        dir="ltr"
        autoComplete="tel"
        required
        aria-required="true"
        aria-invalid={showError ? 'true' : undefined}
        aria-describedby={showError ? errorId : undefined}
        placeholder="050-123-4567"
        value={display}
        onChange={(event) => handleChange(event.target.value)}
        onPaste={(event) => {
          event.preventDefault();
          const pasted = event.clipboardData.getData('text');
          handleChange(pasted);
        }}
        onBlur={() => setTouched(true)}
        className="ltr-inline mt-1 min-h-11 w-full max-w-64 rounded-md border border-line px-3 py-2"
      />
      {showError && !result.ok && <FieldError id={errorId}>{result.message}</FieldError>}
    </div>
  );
}
